import { CLIENT_DEFAULT_VALUES, HELPER_TEXT, LABELS, PLACEHOLDERS } from '@constants'
import { useStore } from '@hooks/useStore'
import { CheckCircle, Close, ErrorOutline, Search, TaskAlt } from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import Grid2 from '@mui/material/Unstable_Grid2/Grid2'
import { IKvkCompany } from '@api'
import { ISingleClient, ISingleCountry } from '@typings'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { OvFormInput } from 'src/components/core/ov-form-input/ov-form-input'
import { clientValidationSchema, IClientFormData } from '../client/validation/client.form.validation'
import { KvkPrefilledProvider, useKvkPrefilled } from '../client/client.kvk.search'

interface InlineClientCreateFormProps {
  onClientCreated: (newClient: ISingleClient) => void
  onCancel: () => void
  onDirtyChange?: (isDirty: boolean) => void
}

// Inner component - has access to KvkPrefilledContext and the form's FormProvider
const InlineClientCreateFormInner = observer(
  ({ onClientCreated, onCancel, onDirtyChange }: InlineClientCreateFormProps) => {
    const { client, shared, kvk, company } = useStore()
    const { markFieldAsPrefilled } = useKvkPrefilled()

    // Load countries on mount if not already loaded
    useEffect(() => {
      if (shared.countries.length === 0) {
        shared.getCountries()
      }
    }, [shared])

    const netherlandsCountry = shared.countries.find(c => c.code === 'NL') ?? {
      id: 0,
      name: 'Netherlands',
      code: 'NL',
    }

    const form = useForm<IClientFormData>({
      resolver: yupResolver(clientValidationSchema()) as any,
      mode: 'all',
      defaultValues: {
        ...CLIENT_DEFAULT_VALUES,
        country: netherlandsCountry,
        company_id: company.current_company?.id ? String(company.current_company.id) : '',
      },
    })

    const {
      control,
      handleSubmit,
      watch,
      getValues,
      setValue,
      reset,
      formState: { isDirty, isSubmitting, errors },
    } = form

    const clientType = watch('client_type')
    const tax = watch('tax')
    const isZakelijk = clientType === 'zakelijk'
    const FormInput = OvFormInput<IClientFormData>

    const [submitError, setSubmitError] = useState<string | null>(null)

    // KVK search state
    const [kvkInputValue, setKvkInputValue] = useState('')
    const [kvkSearchQuery, setKvkSearchQuery] = useState('')
    const [kvkSelectedValue, setKvkSelectedValue] = useState<IKvkCompany | null>(null)
    const lastSelectedKvkRef = useRef<string | null>(null)

    // Notify parent of dirty state changes so navigation blocker can aggregate
    useEffect(() => {
      onDirtyChange?.(isDirty)
    }, [isDirty, onDirtyChange])

    // Reset VAT validation when tax field changes
    useEffect(() => {
      client.resetVatValidation()
    }, [tax, client])

    // Update country default once countries are loaded from the store
    useEffect(() => {
      if (shared.countries.length > 0) {
        const nl = shared.countries.find(c => c.code === 'NL')
        if (nl) {
          setValue('country', nl, { shouldDirty: false })
        }
      }
    }, [shared.countries, setValue])

    // KVK debounced search
    useEffect(() => {
      const timer = setTimeout(() => {
        if (kvkInputValue) {
          setKvkSearchQuery(kvkInputValue)
        }
      }, 500)
      return () => clearTimeout(timer)
    }, [kvkInputValue])

    useEffect(() => {
      if (kvkSearchQuery) {
        kvk.searchCompany(kvkSearchQuery)
      }
    }, [kvkSearchQuery, kvk])

    const applyCountryFromName = (countryName: string | null) => {
      if (!countryName) return
      const lowerName = countryName.toLowerCase().trim()
      let country = shared.countries.find(c => c.name.toLowerCase() === lowerName)
      if (!country) {
        country = shared.countries.find(
          c => c.name.toLowerCase().includes(lowerName) || lowerName.includes(c.name.toLowerCase())
        )
      }
      if (country) {
        setValue('country', country, { shouldDirty: true })
        markFieldAsPrefilled('country')
      }
    }

    const handleKvkSelectCompany = async (company: IKvkCompany | null) => {
      if (!company) return
      lastSelectedKvkRef.current = company.kvk_number

      // Populate form fields from search result
      // Note: We use /zoeken endpoint only (no /basisprofielen call) to avoid €0.02/request cost
      // The /zoeken endpoint already returns complete address data
      if (company.name) {
        setValue('name', company.name, { shouldDirty: true })
        markFieldAsPrefilled('name')
      }
      if (company.street) {
        setValue('street', company.street, { shouldDirty: true })
        markFieldAsPrefilled('street')
      }
      if (company.house_number) {
        setValue('house_number', company.house_number, { shouldDirty: true })
        markFieldAsPrefilled('house_number')
      }
      if (company.house_number_addition) {
        setValue('house_number_addition', company.house_number_addition, { shouldDirty: true })
        markFieldAsPrefilled('house_number_addition')
      }
      if (company.postal_code) {
        setValue('postal_code', company.postal_code, { shouldDirty: true })
        markFieldAsPrefilled('postal_code')
      }
      if (company.city) {
        setValue('city', company.city, { shouldDirty: true })
        markFieldAsPrefilled('city')
      }
      if (company.kvk_number) {
        setValue('coc', company.kvk_number, { shouldDirty: true })
        markFieldAsPrefilled('coc')
      }
      applyCountryFromName(company.country)

      // Clear search
      setKvkInputValue('')
      setKvkSearchQuery('')
      setKvkSelectedValue(null)
    }

    const handleValidateVat = async () => {
      const vatNumber = getValues('tax')
      await client.validateVat(vatNumber)
    }

    const onSubmit: SubmitHandler<IClientFormData> = async (values) => {
      setSubmitError(null)
      try {
        // Inject company_id from current company (required by backend)
        // Also ensure country_id is properly extracted (id: 0 is invalid)
        const clientData = {
          ...values,
          company_id: company.current_company?.id ? String(company.current_company.id) : '',
          country_id: values.country?.id && values.country.id > 0 ? values.country.id : undefined
        }
        const newClient = await client.create(clientData as any)
        await client.getAll()
        // Reset inline form after success
        reset({ ...CLIENT_DEFAULT_VALUES, country: netherlandsCountry } as any)
        onClientCreated(newClient)
      } catch (e: any) {
        setSubmitError(e?.message || 'Er is een fout opgetreden bij het opslaan van de klant.')
      }
    }

    return (
      <FormProvider {...form}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            mt: 1,
            bgcolor: 'background.default',
          }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}>
            <Typography variant="h6">{LABELS.CLIENT_CREATE}</Typography>
            <Tooltip title={LABELS.CANCEL}>
              <Button
                size="small"
                variant="text"
                onClick={onCancel}
                endIcon={<Close />}>
                {LABELS.CANCEL}
              </Button>
            </Tooltip>
          </Stack>

          {submitError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <Stack
            direction="column"
            gap={2}>
            {/* Client type toggle */}
            <Stack
              direction="column"
              gap={1}>
              <Typography
                variant="body2"
                color="text.secondary">
                {LABELS.CLIENT_TYPE}
              </Typography>
              <Controller
                name="client_type"
                control={control}
                render={({ field }) => (
                  <ToggleButtonGroup
                    value={field.value}
                    exclusive
                    onChange={(_, newValue) => {
                      if (newValue !== null) {
                        field.onChange(newValue)
                      }
                    }}
                    size="small">
                    <ToggleButton value="zakelijk">{LABELS.ZAKELIJK}</ToggleButton>
                    <ToggleButton value="particulier">{LABELS.PARTICULIER}</ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Stack>

            {/* KVK Search - only shown for zakelijk */}
            {isZakelijk && (
              <Box>
                <Autocomplete
                  options={kvk.list || []}
                  value={kvkSelectedValue}
                  loading={kvk.loading}
                  inputValue={kvkInputValue}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input') {
                      setKvkInputValue(newInputValue)
                    } else if (reason === 'clear') {
                      setKvkInputValue('')
                    }
                  }}
                  onChange={(_, value) => handleKvkSelectCompany(value)}
                  getOptionLabel={(option: IKvkCompany) => option.name || ''}
                  isOptionEqualToValue={(option: IKvkCompany, value: IKvkCompany) =>
                    option.kvk_number === value.kvk_number
                  }
                  filterOptions={x => x}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Zoek bedrijf in KVK (naam of KVK-nummer)"
                      placeholder="Typ om te zoeken..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                        endAdornment: (
                          <>
                            {kvk.loading ? (
                              <CircularProgress
                                color="inherit"
                                size={20}
                              />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option: IKvkCompany) => (
                    <Box
                      component="li"
                      {...props}>
                      <Stack
                        spacing={0.5}
                        sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          fontWeight={500}>
                          {option.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary">
                          KVK: {option.kvk_number}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary">
                          {option.street} {option.house_number}
                          {option.house_number_addition ? ` ${option.house_number_addition}` : ''},{' '}
                          {option.postal_code} {option.city}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  noOptionsText={
                    kvkInputValue.length < 2
                      ? 'Typ minimaal 2 karakters om te zoeken'
                      : kvk.loading
                      ? 'Zoeken...'
                      : 'Geen resultaten gevonden'
                  }
                  clearOnBlur
                  clearOnEscape
                />
                {(kvkInputValue.length < 2 || kvk.loading) && kvk.list.length === 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}>
                    Of vul de gegevens handmatig in onderstaande velden
                  </Typography>
                )}
              </Box>
            )}

            <Grid2
              container
              spacing={2}>
              <Grid2
                xs={12}
                sm={isZakelijk ? 6 : 12}>
                <Stack
                  direction="column"
                  gap={2}>
                  <FormInput
                    name="name"
                    label={LABELS.CLIENT_NAME}
                  />
                  <FormInput
                    name="street"
                    label={LABELS.STREET}
                    placeholder={PLACEHOLDERS.STREET}
                  />
                  <FormInput
                    name="house_number"
                    label={LABELS.HOUSE_NUMBER}
                    placeholder={PLACEHOLDERS.HOUSE_NUMBER}
                  />
                  <FormInput
                    name="house_number_addition"
                    label={LABELS.HOUSE_NUMBER_ADDITION}
                  />
                  <FormInput
                    name="postal_code"
                    label={LABELS.POSTAL_CODE}
                    placeholder={PLACEHOLDERS.POSTAL_CODE}
                    helperText={HELPER_TEXT.POSTAL_CODE_NL}
                  />
                  <FormInput
                    name="city"
                    label={LABELS.CITY}
                  />
                  <FormInput
                    type="autocomplete"
                    name="country"
                    isOptionEqualToValue={(option: ISingleCountry, val: ISingleCountry) => option.id === val.id}
                    optionLabelKey="name"
                    label={LABELS.COUNTRY}
                    options={shared.countries}
                  />
                  <FormInput
                    name="email"
                    label={LABELS.EMAIL}
                    placeholder={PLACEHOLDERS.EMAIL}
                    helperText={HELPER_TEXT.EMAIL}
                  />
                </Stack>
              </Grid2>

              {isZakelijk && (
                <Grid2
                  xs={12}
                  sm={6}>
                  <Stack
                    direction="column"
                    gap={2}>
                    <FormInput
                      label={LABELS.COC}
                      name="coc"
                      placeholder={PLACEHOLDERS.KVK_NUMBER}
                      helperText={HELPER_TEXT.KVK_NUMBER}
                    />
                    <FormInput
                      label={LABELS.TAX_NUMBER}
                      name="tax"
                      placeholder={PLACEHOLDERS.BTW_ID_NL}
                      error={!!client.vatError}
                      helperText={
                        client.vatError ||
                        (client.vatValid
                          ? `BTW-nummer is geldig${client.vatCompanyName ? ` - ${client.vatCompanyName}` : ''}`
                          : 'Klik op de knop om het BTW-nummer te valideren bij het VIES systeem (alleen EU-landen)')
                      }
                      FormHelperTextProps={{
                        sx: {
                          color: client.vatError
                            ? 'error.main'
                            : client.vatValid
                            ? 'success.main'
                            : 'text.secondary',
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {client.vatLoading && <CircularProgress size={20} />}
                            {!client.vatLoading && client.vatValid && <CheckCircle color="success" />}
                            {!client.vatLoading && client.vatError && <ErrorOutline color="error" />}
                            {!client.vatLoading && !client.vatValid && !client.vatError && (
                              <Tooltip title="Valideer BTW-nummer bij VIES">
                                <Button
                                  size="small"
                                  onClick={handleValidateVat}
                                  disabled={!watch('tax') || watch('tax').length < 4}
                                  sx={{ minWidth: 'auto', px: 1 }}>
                                  <TaskAlt />
                                </Button>
                              </Tooltip>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary">
                      {HELPER_TEXT.BTW_KVK_HINT}
                    </Typography>
                    <FormInput
                      name="code"
                      label={LABELS.CLIENT_CODE}
                    />
                    <FormInput
                      name="hourly_rate"
                      label={LABELS.PRICE_PER_HOUR}
                    />
                  </Stack>
                </Grid2>
              )}
            </Grid2>

            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              mt={1}>
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={isSubmitting}>
                {LABELS.CANCEL}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleSubmit(onSubmit)()
                }}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}>
                {LABELS.CLIENT_SAVE}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </FormProvider>
    )
  }
)

// Public export: provides KVK prefilled context
export const InlineClientCreateForm = (props: InlineClientCreateFormProps) => {
  return (
    <KvkPrefilledProvider>
      <InlineClientCreateFormInner {...props} />
    </KvkPrefilledProvider>
  )
}
